import { useCallback, useState } from 'react';

import { parseQuantity } from '../lib/parseQuantity';

import type { MoveEquipmentInventoryQuantityRequest } from './types';

export const useMoveEquipmentInventoryQuantity = () => {
    const [request, setRequest] = useState<MoveEquipmentInventoryQuantityRequest | null>(null);
    const [value, setValue] = useState('');
    const [isPending, setIsPending] = useState(false);

    const requestMove = useCallback((nextRequest: MoveEquipmentInventoryQuantityRequest) => {
        setRequest(nextRequest);
        setValue(String(nextRequest.amount).replace('.', ','));
    }, []);

    const cancelMove = useCallback(() => {
        if (isPending) {
            return;
        }

        request?.onCancel?.();
        setRequest(null);
        setValue('');
    }, [isPending, request]);

    const quantity = parseQuantity(value);
    const isValid = Boolean(request) && quantity > 0 && quantity <= request.amount;

    const submitMove = useCallback(async () => {
        if (!request || !isValid) {
            return;
        }

        setIsPending(true);

        try {
            if (quantity === request.amount) {
                await request.onMoveFully();
            } else {
                await request.onMovePartially(quantity);
            }

            setRequest(null);
            setValue('');
        } catch {
            return;
        } finally {
            setIsPending(false);
        }
    }, [isValid, quantity, request]);

    return {
        request,
        value,
        quantity,
        isValid,
        isPending,
        setValue,
        requestMove,
        cancelMove,
        submitMove,
    };
};
